import {WordScoreResult, WordScoresResponse} from "data/models/shared/word-scores";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {useObjectVal} from "react-firebase-hooks/database";
import {MultiSelectValue} from "data/models/survey/multi-choice";
import {ref} from "firebase/database";
import {database, functions} from "hooks/firebase";
import {useEffect, useMemo, useState} from "react";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {httpsCallable} from "firebase/functions";
import {WordScoresRequest} from "data/models/shared/word-scores";
import {ChoiceValue} from "data/models/survey/choice-base";
import {multiSelectValues} from "helpers/choice-values";

export type WordScoreResultWithValue = WordScoreResult & {
  value: string;
}


export function useWordScores(): [WordScoreResultWithValue[] | null, boolean, Error | null] {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const canvaAssetsPath = useMemo(() => `user-data/${user?.uid ?? ""}/user-surveys/canva-assets/all-features`, [user]);
  const [rankBenefitsAndTraits, rankBenefitsAndTraitsLoading, rankBenefitsAndTraitsError] =
      useObjectVal<ChoiceValue[]>((user && ref(database, `${canvaAssetsPath}/what-makes-you-special/rank-benefits-and-traits`)) ?? null);
  const [customersClientsDescribed, customersClientsDescribedLoading, customersClientsDescribedError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${canvaAssetsPath}/your-customers/customers-clients-described`)) ?? null);

  const [wordScores, setWordScores] = useState<WordScoreResultWithValue[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const anyLoading = (
      userLoading
      || rankBenefitsAndTraitsLoading
      || customersClientsDescribedLoading
  );
  const anError = (
      userError
      ?? rankBenefitsAndTraitsError
      ?? customersClientsDescribedError
  );

  useEffect(() => {
    if (!isNullOrUndefined(anError)) {
      setError(anError);
      return;
    }
    if (anyLoading) {
      return;
    }
    if (
        isNullOrUndefined(rankBenefitsAndTraits)
        || isNullOrUndefined(customersClientsDescribed)
    ) {
      return;
    }

    const productTraitsValues = rankBenefitsAndTraits ?? [];

    const wordScoresRequest: WordScoresRequest = {
      productTraits: [...new Set([
        ...productTraitsValues.map(({key}) => key),
      ])],
      marketTraits: multiSelectValues(customersClientsDescribed) ?? [],
    };

    const wordScoresFunction = httpsCallable<WordScoresRequest, WordScoresResponse>(functions, 'wordScores');

    wordScoresFunction(wordScoresRequest).then(result => {
      setWordScores(result.data.map((wordScore => ({
        ...wordScore,
        value: productTraitsValues.find(({key}) => key === wordScore.word)?.value ?? wordScore.word,
      }))));
      setError(null);
    }).catch((error) => {
      setError(error as Error);
    });
  }, [
    anyLoading, anError,
    rankBenefitsAndTraits,
    customersClientsDescribed,
  ]);

  return [wordScores, anyLoading, error];
}
